/**
 * Live Dispatch Map — wow-screen #2 from figma-scope-v2.
 *
 * Spatial operating interface, NOT a "map widget". 70-75% of the
 * viewport is map; left rail is filter+legend; right panel slides in
 * with vehicle detail when a marker is clicked.
 *
 * Tile choice: OpenStreetMap (free, no key). Industrial deployments
 * can swap to Mapbox/Yandex by replacing the `_TILE_URL` constant —
 * everything else stays the same.
 *
 * Live updates: subscribe to `vehicle.coords_updated` on ws-gateway.
 * Each event updates exactly one marker's LatLng + popup content; we
 * do NOT re-render the whole map for every ping.
 *
 * Imperative Leaflet (not react-leaflet) on purpose:
 *   - Fewer deps
 *   - Better control over per-marker performance with high-frequency
 *     position updates (10+ pings/sec on busy fleets)
 *   - React handles surrounding UI; the map div is a managed ref
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Activity, Filter, MapPin, RefreshCw, Truck, Wrench,
} from "lucide-react";
import L from "leaflet";
import { Badge, Button, Card, CardHeader, cn } from "@vseh/ui";

import {
  vehiclesApi, type VehicleDto, type VehicleStatus,
} from "../shared/api";
import { useEventStream, useFetch, type EventEnvelope } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { ErrorBlock, LoadingBlock } from "../shared/components/States";


// ---------------------------------------------------------------------------
// Tile config + default view
// ---------------------------------------------------------------------------


const _TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
const _TILE_ATTRIB =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Default centre: Krasnodar — large fleet area in our demo data.
// When real data lands the map auto-fits to the bounding box of all
// vehicles with coords.
const _DEFAULT_CENTRE: [number, number] = [45.0355, 38.9753];
const _DEFAULT_ZOOM = 6;


// Status pills below the header. Keys (rather than literals) so
// useT() can resolve them per render — a language toggle re-renders
// the pill labels without a reload.
const STATUS_FILTER_KEYS: { value: VehicleStatus | "all"; tone: "neutral"|"ai"|"danger"|"heritage"; key: string }[] = [
  { value: "all",         tone: "neutral",  key: "dispatch.filter.all"         },
  { value: "idle",        tone: "neutral",  key: "dispatch.filter.idle"        },
  { value: "busy",        tone: "ai",       key: "dispatch.filter.busy"        },
  { value: "offline",     tone: "danger",   key: "dispatch.filter.offline"     },
  { value: "maintenance", tone: "heritage", key: "dispatch.filter.maintenance" },
];


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------


export function DispatchMapPage(): JSX.Element {
  const t = useT();
  const [filter, setFilter] = useState<VehicleStatus | "all">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      vehiclesApi.list(filter === "all" ? {} : { status: filter }, { signal }),
    [filter],
  );
  const { data, error, loading, refetch } = useFetch<VehicleDto[]>(
    fetcher, [filter],
  );

  // Live updates — coords + status. We rely on refetch for now;
  // a follow-up sprint can merge per-vehicle deltas in place to avoid
  // the round-trip. At fleet sizes <500 the cost is negligible.
  const onEvent = useCallback((ev: EventEnvelope) => {
    if (ev.event_type?.startsWith("vehicle.")) refetch();
  }, [refetch]);
  useEventStream(onEvent, { subjects: ["vehicle.>"] });

  const selectedVehicle = useMemo(
    () => data?.find(v => v.id === selectedId) ?? null,
    [data, selectedId],
  );

  return (
    <div className="grid grid-cols-12 gap-3">
      <div className="col-span-3">
        <Card>
          <CardHeader
            title={t("dispatch.title")}
            description={t("dispatch.subtitle")}
            actions={
              <Button variant="ghost" size="xs"
                      leftIcon={<RefreshCw className="size-3.5" />}
                      onClick={refetch}>
                {t("common.refresh")}
              </Button>
            }
          />
          <FilterRail value={filter} onChange={setFilter} />
          <Legend />
          <CountsRow rows={data ?? []} />
        </Card>
      </div>

      <div className={cn("relative", selectedVehicle ? "col-span-6" : "col-span-9")}>
        <Card className="!p-0 overflow-hidden">
          {loading && !data && <div className="p-6"><LoadingBlock /></div>}
          {error && <div className="p-6"><ErrorBlock error={error} onRetry={refetch} /></div>}
          {data && (
            <MapCanvas
              vehicles={data}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          )}
        </Card>
      </div>

      {selectedVehicle && (
        <div className="col-span-3">
          <VehicleSidePanel
            vehicle={selectedVehicle}
            onClose={() => setSelectedId(null)}
          />
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// MapCanvas — imperative Leaflet, React owns the parent only
// ---------------------------------------------------------------------------


interface MapCanvasProps {
  vehicles: VehicleDto[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}


function MapCanvas({ vehicles, selectedId, onSelect }: MapCanvasProps): JSX.Element {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.CircleMarker>>(new Map());
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // --- Map init (once) ---------------------------------------------------
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: _DEFAULT_CENTRE,
      zoom: _DEFAULT_ZOOM,
      // We keep zoom control + attribution but ditch fullscreen / draw —
      // operators want focus, not toy buttons.
      zoomControl: true,
      attributionControl: true,
    });

    L.tileLayer(_TILE_URL, {
      attribution: _TILE_ATTRIB,
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;

    // Clicking empty map deselects current pin.
    map.on("click", () => onSelectRef.current(null));

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
  }, []);

  // --- Sync markers with vehicles prop ----------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const seen = new Set<string>();
    const withCoords = vehicles.filter(
      v => v.current_lat != null && v.current_lng != null,
    );

    for (const v of withCoords) {
      seen.add(v.id);
      const latlng: [number, number] = [v.current_lat!, v.current_lng!];
      const existing = markersRef.current.get(v.id);

      if (existing) {
        existing.setLatLng(latlng);
        existing.setStyle(_styleFor(v.status, v.id === selectedId));
        // Update popup content in case status / display changed.
        existing.getPopup()?.setContent(_popupHtml(v, t("vehicles.unit.kmh")));
      } else {
        const marker = L.circleMarker(latlng, _styleFor(v.status, v.id === selectedId))
          .addTo(map)
          .bindPopup(_popupHtml(v, t("vehicles.unit.kmh")), { closeButton: false })
          .on("click", () => onSelectRef.current(v.id));
        markersRef.current.set(v.id, marker);
      }
    }

    // Remove markers for vehicles no longer in the filtered list.
    for (const [id, marker] of markersRef.current) {
      if (!seen.has(id)) {
        marker.remove();
        markersRef.current.delete(id);
      }
    }

    // Auto-fit to bounding box of all markers — only on first render
    // and when the selection clears (avoids fighting the user's pan).
    if (withCoords.length > 0 && selectedId === null && markersRef.current.size > 0) {
      const bounds = L.latLngBounds(
        withCoords.map(v => [v.current_lat!, v.current_lng!] as [number, number]),
      );
      // Pad so markers aren't flush with map edges; max-zoom cap so a
      // single vehicle doesn't zoom us to street level.
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
    }
  }, [vehicles, selectedId]);

  // --- Selected pin styling + fly-to ------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId) return;
    const marker = markersRef.current.get(selectedId);
    if (!marker) return;
    marker.openPopup();
    map.flyTo(marker.getLatLng(), Math.max(map.getZoom(), 11), {
      duration: 0.4,
    });
  }, [selectedId]);

  return (
    <div
      ref={containerRef}
      className="h-[70vh] w-full"
      aria-label={t("dispatch.map.aria")}
    />
  );
}


// ---------------------------------------------------------------------------
// Marker style + popup content — pure helpers
// ---------------------------------------------------------------------------


function _styleFor(status: VehicleStatus, selected: boolean): L.CircleMarkerOptions {
  // Status → CSS-var color so dark mode (figma-scope-v2 §13) can re-style
  // without code changes. Selected markers get a thick outer ring +
  // higher z-index.
  const fill = {
    idle:        "rgb(100 116 139)",   // base-500
    busy:        "rgb(99 102 241)",    // ai-500
    offline:     "rgb(220 38 38)",     // danger-600
    maintenance: "rgb(217 119 6)",     // amber-600
  }[status];
  return {
    radius: selected ? 11 : 8,
    weight: selected ? 4 : 2,
    color:  selected ? "rgb(15 23 42)" : "rgb(255 255 255)",
    fillColor: fill,
    fillOpacity: 0.9,
    bubblingMouseEvents: false,
  };
}


function _popupHtml(v: VehicleDto, kmh: string): string {
  const speed = v.current_speed_kmh != null
    ? `${Math.round(v.current_speed_kmh)} ${kmh}`
    : "—";
  return `
    <div style="min-width: 180px; font-family: inherit;">
      <div style="font-weight: 600; font-size: 13px;">${_escape(v.display_name)}</div>
      <div style="margin-top: 4px; font-size: 11px; color: rgb(100 116 139);">
        ${_escape(v.vehicle_type)} · ${_escape(v.license_plate ?? "—")}
      </div>
      <div style="margin-top: 4px; font-size: 11px;">
        <span style="font-weight: 500;">${_escape(v.status)}</span>
        · ${speed}
      </div>
    </div>
  `;
}


function _escape(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replaceAll("&",  "&amp;")
    .replaceAll("<",  "&lt;")
    .replaceAll(">",  "&gt;")
    .replaceAll('"',  "&quot;")
    .replaceAll("'",  "&#39;");
}


// ---------------------------------------------------------------------------
// Side panel — vehicle detail
// ---------------------------------------------------------------------------


function VehicleSidePanel({
  vehicle, onClose,
}: { vehicle: VehicleDto; onClose: () => void }): JSX.Element {
  const t = useT();
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Truck className="size-4 text-base-600" />
            {vehicle.display_name}
          </span>
        }
        description={
          <span className="text-2xs text-base-500">
            {vehicle.vehicle_type}
            {vehicle.license_plate && ` · ${vehicle.license_plate}`}
          </span>
        }
        actions={
          <Button variant="ghost" size="xs" onClick={onClose}>
            ×
          </Button>
        }
      />
      <div className="space-y-2 text-xs">
        <DetailRow label={t("dispatch.detail.status")} value={<StatusBadge status={vehicle.status} />} />
        <DetailRow
          label={t("dispatch.detail.coords")}
          value={
            vehicle.current_lat != null && vehicle.current_lng != null ? (
              <span className="font-mono tabular-nums">
                {vehicle.current_lat.toFixed(5)}, {vehicle.current_lng.toFixed(5)}
              </span>
            ) : (
              <span className="text-base-400">{t("dispatch.detail.no_data")}</span>
            )
          }
        />
        <DetailRow
          label={t("dispatch.detail.speed")}
          value={
            vehicle.current_speed_kmh != null
              ? <span className="tabular-nums">{Math.round(vehicle.current_speed_kmh)} {t("vehicles.unit.kmh")}</span>
              : <span className="text-base-400">—</span>
          }
        />
        <DetailRow
          label={t("dispatch.detail.heading")}
          value={
            vehicle.current_heading_deg != null
              ? <span className="tabular-nums">{Math.round(vehicle.current_heading_deg)}°</span>
              : <span className="text-base-400">—</span>
          }
        />
        <DetailRow
          label="Last ping"
          value={
            vehicle.last_seen_at
              ? <span className="tabular-nums">{new Date(vehicle.last_seen_at).toLocaleString("ru-RU")}</span>
              : <span className="text-base-400">—</span>
          }
        />
      </div>
    </Card>
  );
}


function DetailRow({ label, value }: { label: string; value: JSX.Element }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 py-1 border-b border-base-100 last:border-0">
      <span className="text-2xs uppercase tracking-wide text-base-500">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}


function StatusBadge({ status }: { status: VehicleStatus }): JSX.Element {
  const tone = ({
    idle:        "neutral"  as const,
    busy:        "ai"       as const,
    offline:     "danger"   as const,
    maintenance: "heritage" as const,
  }[status] ?? "neutral");
  const icon = status === "maintenance" ? <Wrench className="mr-1 size-3" /> : null;
  return <Badge tone={tone} dot>{icon}{status}</Badge>;
}


// ---------------------------------------------------------------------------
// Sidebar — filter + legend + counts
// ---------------------------------------------------------------------------


function FilterRail(props: {
  value: VehicleStatus | "all";
  onChange: (v: VehicleStatus | "all") => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-base-500">
        <Filter className="size-3" />
        {t("dispatch.filter.label")}
      </div>
      <div className="flex flex-col gap-0.5">
        {STATUS_FILTER_KEYS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => props.onChange(opt.value)}
            className={cn(
              "flex items-center gap-2 rounded-sm px-2 py-1 text-xs font-medium transition-colors",
              props.value === opt.value
                ? "bg-primary-900 text-white"
                : "text-base-700 hover:bg-base-100",
            )}
          >
            <span className={cn(
              "size-2 rounded-full",
              opt.value === "all"          ? "bg-base-400"
                : opt.value === "idle"        ? "bg-base-500"
                : opt.value === "busy"        ? "bg-ai-500"
                : opt.value === "offline"     ? "bg-danger-600"
                : "bg-amber-600",
            )} />
            {t(opt.key)}
          </button>
        ))}
      </div>
    </div>
  );
}


function Legend(): JSX.Element {
  const t = useT();
  return (
    <div className="mb-3 border-t border-base-100 pt-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-base-500">
        <MapPin className="size-3" />
        {t("dispatch.legend")}
      </div>
      <ul className="space-y-1 text-2xs text-base-600">
        <li className="flex items-center gap-2">
          <span className="inline-block size-2 rounded-full bg-base-500" /> idle
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-block size-2 rounded-full bg-ai-500" /> busy
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-block size-2 rounded-full bg-danger-600" /> offline
        </li>
        <li className="flex items-center gap-2">
          <span className="inline-block size-2 rounded-full bg-amber-600" /> maintenance
        </li>
      </ul>
    </div>
  );
}


function CountsRow({ rows }: { rows: VehicleDto[] }): JSX.Element {
  const counts = useMemo(() => {
    const out = { total: rows.length, idle: 0, busy: 0, offline: 0, maintenance: 0, no_gps: 0 };
    for (const v of rows) {
      if (v.status === "idle") out.idle++;
      else if (v.status === "busy") out.busy++;
      else if (v.status === "offline") out.offline++;
      else if (v.status === "maintenance") out.maintenance++;
      if (v.current_lat == null || v.current_lng == null) out.no_gps++;
    }
    return out;
  }, [rows]);
  const t = useT();
  return (
    <div className="border-t border-base-100 pt-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-base-500">
        <Activity className="size-3" />
        {t("dispatch.counts.title")}
      </div>
      <div className="space-y-0.5 text-xs">
        <CountRow label={t("dispatch.counts.total")}        value={counts.total} />
        <CountRow label={t("dispatch.counts.busy")}         value={counts.busy} />
        <CountRow label={t("dispatch.counts.idle")}         value={counts.idle} />
        <CountRow label={t("dispatch.counts.offline")}      value={counts.offline} tone="danger" />
        <CountRow label={t("dispatch.counts.maintenance")}  value={counts.maintenance} tone="warning" />
        <CountRow label={t("dispatch.counts.no_gps")}       value={counts.no_gps} tone="warning" />
      </div>
    </div>
  );
}


function CountRow(props: {
  label: string; value: number; tone?: "warning" | "danger";
}): JSX.Element {
  const cls = props.tone === "danger"  ? "text-danger-700"
            : props.tone === "warning" ? "text-amber-700"
            : "text-base-800";
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-base-600">{props.label}</span>
      <span className={cn("font-medium tabular-nums", cls)}>{props.value}</span>
    </div>
  );
}
